/**  
 * EASY_DUI_LandingPage_v02h_WIZARD (React + TypeScript + React-Bootstrap)
 * Version: v02h-WIZARD
 * Date: 2025-08-28 (America/Denver)
 *
 * Fixes vs v02g:
 * - VIN length counter restored and visible (live n/17 under input) in wizard + vertical.
 * - Blur-based validation confirmed across all controls (vp() attached as last prop; VIN has explicit onBlur).
 * - Confirmation shows in both modes:
 *    • Wizard: Step 4 appears after successful submit.
 *    • Vertical: Inline confirmation block after submit.
 * 
 *      NOTE: QUICK FIX - UPDATED THE WIZARD CONTROLS SECTION SO THE PREVOUS AND BACK-TO-TOP BUTTONS DO NOT APPEAR ON THE FINAL STEP
 */


// PAYLOAD ==============================
// {
//     "fullName": "Rick Andersen",
//     "email": "test@abc.com",
//     "phone": "3035550180",
//     "state": "LA",
//     "zipCode": "33137",


//     "duiCount": 2,
//     "duiMostRecent": "2025-08-12",
//     "driversLicenseNumber": "abc133",
//     "driversLicenseState": "LA",
//     "dateOfBirth": "2009-08-20",


//     "filingType": "SR22",
//     "filingState": "LA",
//     "ownCar": true,
//     "carYear": "2009",
//     "carMake": "Toyota",
//     "carModel": "Camry",
//     "carVIN": "3KDKSLKDFSKDFKSDK",
//     "carApproximateAnnualMileage": "12,000",
//     "carPrimaryUse": "business",


//     "notes": "super",
//     "utm": {},
//     "domainName": "localhost",
//     "itemCategory": "Customer.dui.quote",
//     "itemClass": "quote.request",
//     "itemType": "dui.landingPage",
//     "clientVersion": "v02h-WIZARD"
// }




import React, { useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Nav,
  Navbar,
  Row,
  Spinner,
  Accordion,
  Carousel,
  InputGroup,
  ProgressBar,
} from "react-bootstrap";

/* ============================================================
   CONFIG
============================================================ */
import { FAQ_Component_v02b_Simple  } from "../../component-library/FAQ_Component_v02b-simple";
// import {  } from "../../component-library/MASTER-COMPONENTS/FAQ_Component_MASTER_v02_WithSUB"
import { duiInsuranceFAQ_CONTENT_v01 } from "./DUI_FAQ_INSURANCE_CONTENT_v01";
import { FAQ_Component_MASTER_v02_WithSUB } from "../../component-library/MASTER-COMPONENTS/FAQ_Component_MASTER_v02_WithSUB"
const VERSION = "v02h-WIZARD";
const SHOW_VERSION = false;
const TEST_MODE = false; // true => logs payload, skips network
const API_URL = "https://wi8zkeven2.execute-api.us-east-1.amazonaws.com/prod/feedback";
const AGENT_PHONE_NUMBER = "(303) 337-4433";
const imagePath = "insurance/images/DUI-Insurance/";

/* ============================================================
   TYPES
============================================================ */
type FilingType = "" | "NOT_SURE" | "SR22" | "FR44";

interface ILeadPayload {
  fullName: string;
  email: string;
  phone: string;
  state: string;
  zipCode: string;

  duiCount: number;
  duiMostRecent: string;

  driversLicenseNumber: string;
  driversLicenseState: string;

  dateOfBirth: string; // DOB

  filingType: FilingType;
  filingState?: string;

  ownCar: boolean;
  carYear?: string;
  carMake?: string;
  carModel?: string;
  carVIN?: string;
  carApproximateAnnualMileage?: string;
  carPrimaryUse?: "commute" | "business" | "pleasure" | "rideshare";

  notes?: string;
  utm?: {
    source?: string;
    medium?: string;
    campaign?: string;
  };

  domainName: string;
  itemCategory: string;
  itemClass: string;
  itemType: string;
  clientVersion: string;
}

/* ============================================================
   HELPERS
============================================================ */
const US_STATES: { code: string; name: string }[] = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" },
];

function digitsOnly(s: string): string {
  return (s || "").replace(/\D+/g, "");
}
function isValidEmail(email: string): boolean {
  return /.+@.+\..+/.test(email);
}
function isValidPhone(raw: string): boolean {
  return digitsOnly(raw).length === 10;
}
function isValidZipCode(raw: string): boolean {
  return digitsOnly(raw).length === 5;
}
function ageFromDOB(yyyy_mm_dd: string): number | null {
  if (!yyyy_mm_dd) return null;
  const d = new Date(yyyy_mm_dd);
  if (d.toString() === "Invalid Date") return null;
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
  return age;
}
function formatPhonePretty(raw: string): string {
  const d = digitsOnly(raw);
  if (d.length !== 10) return raw;
  return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
}

/* ============================================================
   SMALL UI BITS
============================================================ */
const Feature: React.FC<{ title: string; text: string; emoji: string }> = ({ title, text, emoji }) => (
  <Card className="h-100 shadow-sm">
    <Card.Body>
      <div className="display-6" aria-hidden>{emoji}</div>
      <h5 className="mt-3 mb-2">{title}</h5>
      <p className="mb-0 text-body-secondary">{text}</p>
    </Card.Body>
  </Card>
);

const StepCard: React.FC<{ n: number; title: string; text: string }> = ({ n, title, text }) => (
  <div className="d-flex align-items-start gap-3">
    <div className="badge rounded-pill text-bg-primary fs-6 mt-1">{n}</div>
    <div>
      <h6 className="mb-1">{title}</h6>
      <p className="mb-0 text-body-secondary">{text}</p>
    </div>
  </div>
);

const Testimonial: React.FC<{ quote: string; name: string; detail: string }> = ({ quote, name, detail }) => (
  <Card className="h-100 border-0">
    <Card.Body>
      <p className="fst-italic">“{quote}”</p>
      <div className="mt-3">
        <div className="fw-semibold">{name}</div>
        <div className="text-body-secondary">{detail}</div>
      </div>
    </Card.Body>
  </Card>
);

/* ============================================================
   SLIDES
============================================================ */
interface Slide {
  src: string;
  alt: string;
  title: string;
  subline: string;
  smallCaption: string;
}
const slides: Slide[] = [
  {
    src: imagePath + "AdobeStock_1463253262_Preview-FLIP.jpg",
    alt: "Need SR-22 or FR-44 - We will file the right proof of documents for your state.",
    title: "Need SR-22 or FR-44?",
    subline: "We will file the right proof of documents for your state.",
    smallCaption: "Compliance without the headache.",
  },
  {
    src: imagePath + "AdobeStock_1360258027_Preview-FLIP.jpg",
    alt: "Start your Quote in minutes - Submit once. We shop multiple carriers for you.",
    title: "Start your Quote in minutes",
    subline: "Submit once. We shop multiple carriers for you.",
    smallCaption: "Fast, competitive options.",
  },
  {
    src: imagePath + "AdobeStock_370848671_Preview.jpeg",
    alt: "Get Back on the Road - Coverage built for your situation and budget.",
    title: "Get Back on the Road",
    subline: "Coverage built for your situation and budget.",
    smallCaption: "Flexible plans. Friendly experts.",
  },
];

/* ============================================================
   MAIN
============================================================ */
export const EASY_DUI_LandingPage_v02h_WIZARD: React.FC = () => {
  const formRef = useRef<HTMLDivElement | null>(null);
  const [activeSlide, setActiveSlide] = useState(0);

  // Basics
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");

  // DUI
  const [duiCount, setDuiCount] = useState<number>(0);
  const [duiMostRecent, setDuiMostRecent] = useState<string>("");

  // License
  const [driversLicenseNumber, setDriversLicenseNumber] = useState("");
  const [driversLicenseState, setDriversLicenseState] = useState("");

  // DOB
  const [dateOfBirth, setDateOfBirth] = useState<string>("");

  // Filings
  const [filingType, setFilingType] = useState<FilingType>("");
  const [filingState, setFilingState] = useState<string>("");

  // Vehicle
  const [ownCar, setOwnCar] = useState<boolean>(false);
  const [carYear, setCarYear] = useState<string>("");
  const [carMake, setCarMake] = useState<string>("");
  const [carModel, setCarModel] = useState<string>("");
  const [carVIN, setCarVIN] = useState<string>("");
  const [carApproximateAnnualMileage, setCarApproximateAnnualMileage] = useState<string>("");
  const [carPrimaryUse, setCarPrimaryUse] = useState<"commute" | "business" | "pleasure" | "rideshare" | "">("");

  // Misc
  const [notes, setNotes] = useState<string>("");

  // Submit / server state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitOk, setSubmitOk] = useState(false);
  const [serverStatus, setServerStatus] = useState<null | { ok: boolean; status?: number; id?: string; message?: string }>(null);

  // VIN length (live)
  const vinLen = carVIN.trim().length;

  // Mode + wizard
  const [mode, setMode] = useState<"wizard" | "vertical">("wizard");
  const isWizard = mode === "wizard";
  const [currentStep, setCurrentStep] = useState(0);
  const steps = ["Contact Info", "DUI & Filing", "Vehicle & Notes", "Confirmation"] as const;

  /* ---------- Touched + validation helpers (blur-driven) ---------- */
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const isTouched = (name: string) => !!touched[name];
  const markTouched = (name: string) => setTouched((t) => ({ ...t, [name]: true }));

  // Attach to inputs; `pass` is boolean validity for that field
  const vp = (name: string, pass: boolean) => ({
    onBlur: () => markTouched(name),
    isValid: isTouched(name) && pass,
    isInvalid: isTouched(name) && !pass,
    className: isTouched(name) && pass ? "bg-success-subtle" : undefined,
  });

  // Validators
  const age = ageFromDOB(dateOfBirth);
  const V = {
    fullName: fullName.trim().length >= 2,
    email: isValidEmail(email),
    phone: isValidPhone(phone),
    state: state !== "",
    zip: isValidZipCode(zipCode),

    dlNum: driversLicenseNumber.trim().length >= 3,
    dlState: driversLicenseState !== "",

    dob:
      !!dateOfBirth &&
      new Date(dateOfBirth).toString() !== "Invalid Date" &&
      new Date(dateOfBirth) <= new Date() &&
      (age ?? 0) >= 16 && (age ?? 200) <= 100,

    duiDate:
      !!duiMostRecent &&
      new Date(duiMostRecent).toString() !== "Invalid Date" &&
      new Date(duiMostRecent) <= new Date(),
    duiCount: Number.isFinite(duiCount),

    filingType: filingType !== "",
    filingState: filingType === "NOT_SURE" ? true : filingState !== "",

    carYear: carYear.trim().length >= 4,
    carMake: carMake.trim().length >= 2,
    carModel: carModel.trim().length >= 1,
    carVIN_validIfPresent: carVIN.trim() === "" || vinLen === 17,
    carMiles: carApproximateAnnualMileage.trim().length >= 1, // optional -> show green if non-empty
    carUse: carPrimaryUse !== "",
  };

  // Overall validity
  const valid = useMemo(() => {
    const baseOk =
      V.fullName && V.email && V.phone && V.state && V.zip &&
      V.duiCount && V.duiDate &&
      V.dlNum && V.dlState && V.dob;

    const filingOk = V.filingState;

    const vehicleOk = ownCar
      ? V.carYear && V.carMake && V.carModel && V.carVIN_validIfPresent && V.carUse && V.carMiles
      : true;

    return baseOk && filingOk && vehicleOk;
  }, [V, ownCar]);

  // Wizard step checks
  function isStepValid(stepIdx: number): boolean {
    switch (stepIdx) {
      case 0:
        return V.fullName && V.email && V.phone && V.state && V.zip && V.dlNum && V.dlState && V.dob;
      case 1:
        return V.duiCount && V.duiDate && V.filingType && V.filingState;
      case 2:
        return ownCar ? (V.carYear && V.carMake && V.carModel && V.carVIN_validIfPresent && V.carUse && V.carMiles) : true;
      case 3:
        return submitOk === true;
      default:
        return false;
    }
  }

  const isFormValid = () => valid;

  const goNext = () => {
    // Touch the fields for the current step before advancing
    const touchMany = (names: string[]) =>
      setTouched((t) => names.reduce((acc, n) => ({ ...acc, [n]: true }), t));

    if (currentStep === 0) {
      touchMany(["fullName", "email", "phone", "state", "zip", "dlNum", "dlState", "dob"]);
    } else if (currentStep === 1) {
      touchMany(["duiCount", "duiDate", "filingType", "filingState"]);
    } else if (currentStep === 2 && ownCar) {
      touchMany(["carYear", "carMake", "carModel", "carVIN", "carUse", "carMiles"]);
    }

    if (!isStepValid(currentStep)) return;
    setCurrentStep((s) => Math.min(3, s + 1));
  };
  const goBack = () => setCurrentStep((s) => Math.max(0, s - 1));

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitOk(false);
    setServerStatus(null);

    // Touch all fields before final submit
    setTouched((t) => ({
      ...t,
      fullName: true, email: true, phone: true, state: true, zip: true,
      dlNum: true, dlState: true, dob: true,
      duiCount: true, duiDate: true, filingType: true, filingState: true,
      ...(ownCar ? { carYear: true, carMake: true, carModel: true, carVIN: true, carUse: true, carMiles: true } : {}),
    }));

    if (!valid) {
      setSubmitError("Please complete all required fields.");
      return;
    }

    const payload_DATA: ILeadPayload = {
      fullName: fullName.trim(),
      email: email.trim(),
      phone: digitsOnly(phone),
      state: String(state),
      zipCode: String(zipCode),
      duiCount: Number(duiCount),
      duiMostRecent: String(duiMostRecent),
      driversLicenseNumber: driversLicenseNumber.trim(),
      driversLicenseState,
      dateOfBirth: String(dateOfBirth),
      filingType,
      filingState: filingType === "NOT_SURE" ? undefined : filingState,
      ownCar: Boolean(ownCar),
      carYear: ownCar ? carYear.trim() : undefined,
      carMake: ownCar ? carMake.trim() : undefined,
      carModel: ownCar ? carModel.trim() : undefined,
      carVIN: ownCar ? (carVIN.trim() ? carVIN.trim() : undefined) : undefined,
      carApproximateAnnualMileage: ownCar ? carApproximateAnnualMileage.trim() : undefined,
      carPrimaryUse: ownCar && carPrimaryUse ? carPrimaryUse : undefined,
      notes: notes?.trim() || undefined,
      utm: {
        source: new URLSearchParams(window.location.search).get("utm_source") || undefined,
        medium: new URLSearchParams(window.location.search).get("utm_medium") || undefined,
        campaign: new URLSearchParams(window.location.search).get("utm_campaign") || undefined,
      },
      domainName: String(window.location.hostname),
      itemCategory: "Customer.dui.quote",
      itemClass: "quote.request",
      itemType: "dui.landingPage",
      clientVersion: String(VERSION),
    };

    try {
      setSubmitting(true);

      if (TEST_MODE) {
        console.log("[TEST_MODE] Submitting payload:", payload_DATA);
        await new Promise((r) => setTimeout(r, 700));
        setServerStatus({ ok: true, status: 200, id: "TEST-" + Math.random().toString(36).slice(2, 8) });
        setSubmitOk(true);
        if (isWizard) setCurrentStep(3);
        return;
      }

      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload_DATA),
        mode: "cors",
      });

      let resJson: any = null;
      try {
        resJson = await res.json();
      } catch {}

      if (!res.ok) {
        const msg = (resJson && (resJson.error || resJson.message)) || `Request failed: ${res.status}`;
        setServerStatus({ ok: false, status: res.status, message: msg });
        throw new Error(msg);
      }

      setServerStatus({ ok: true, status: res.status, id: resJson?.id || resJson?.requestId });
      setSubmitOk(true);
      if (isWizard) setCurrentStep(3);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setSubmitError(msg);
      setSubmitOk(false);
    } finally {
      setSubmitting(false);
    }
  };

  // Confirmation block (reused for wizard step 4 and vertical mode)
  const ConfirmationBlock: React.FC = () => (
    <fieldset className="border rounded-3 p-3 bg-body-secondary">
      <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
        <strong>Confirmation</strong>
      </legend>

      {submitOk ? (
        <>
          <Alert variant="success" className="mb-3">
            <div className="fw-semibold mb-1">Thank you, a representative will be contacting you.</div>
            <div className="small mb-0">
              {serverStatus?.ok
                ? <>Server status: <strong>OK</strong>{serverStatus?.status ? ` • ${serverStatus.status}` : ""}{serverStatus?.id ? ` • ID: ${serverStatus.id}` : ""}</>
                : "Server status: Unknown"}
            </div>
          </Alert>

          <Row className="g-3">
            <Col md={6}>
              <Card className="h-100">
                <Card.Header className="fw-semibold">Contact</Card.Header>
                <Card.Body className="small">
                  <div><strong>Name:</strong> {fullName}</div>
                  <div><strong>Email:</strong> {email}</div>
                  <div><strong>Phone:</strong> {formatPhonePretty(phone)}</div>
                  <div><strong>State/Zip:</strong> {state} {zipCode}</div>
                  <div><strong>DOB:</strong> {dateOfBirth}</div>
                  <div><strong>DL:</strong> {driversLicenseNumber} ({driversLicenseState})</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="h-100">
                <Card.Header className="fw-semibold">DUI & Filing</Card.Header>
                <Card.Body className="small">
                  <div><strong># DUIs:</strong> {duiCount}</div>
                  <div><strong>Most Recent:</strong> {duiMostRecent}</div>
                  <div><strong>Filing Type:</strong> {filingType || "—"}</div>
                  <div><strong>Filing State:</strong> {filingType === "NOT_SURE" ? "—" : (filingState || "—")}</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={12}>
              <Card>
                <Card.Header className="fw-semibold">Vehicle</Card.Header>
                <Card.Body className="small">
                  {!ownCar ? (
                    <div>Driver does <strong>not</strong> own a car.</div>
                  ) : (
                    <Row xs={1} md={3}>
                      <Col><strong>Year/Make/Model:</strong> {carYear} / {carMake} / {carModel}</Col>
                      <Col><strong>VIN:</strong> {carVIN || "—"}</Col>
                      <Col><strong>Use / Miles:</strong> {carPrimaryUse || "—"} / {carApproximateAnnualMileage || "—"}</Col>
                    </Row>
                  )}
                </Card.Body>
              </Card>
            </Col>
            {notes.trim() && (
              <Col md={12}>
                <Card>
                  <Card.Header className="fw-semibold">Notes</Card.Header>
                  <Card.Body className="small">{notes}</Card.Body>
                </Card>
              </Col>
            )}
          </Row>

          <div className="d-flex justify-content-end gap-2 mt-3">
            <Button
              variant="secondary"
              onClick={() => {
                // start a new quote
                setFullName(""); setEmail(""); setPhone(""); setState(""); setZipCode("");
                setDuiCount(0); setDuiMostRecent("");
                setDriversLicenseNumber(""); setDriversLicenseState("");
                setDateOfBirth("");
                setFilingType(""); setFilingState("");
                setOwnCar(false); setCarYear(""); setCarMake(""); setCarModel("");
                setCarVIN(""); setCarApproximateAnnualMileage(""); setCarPrimaryUse("");
                setNotes("");
                setTouched({});
                setSubmitOk(false);
                setServerStatus(null);
                setCurrentStep(0);
                scrollToForm();
              }}
            >
              Start New Quote
            </Button>
          </div>
        </>
      ) : (
        <Alert variant="warning">We couldn’t confirm the server response. Please go back and try submitting again.</Alert>
      )}
    </fieldset>
  );

  const active = slides[activeSlide];

  /* ------------------------------------------------------------
     UI
  ------------------------------------------------------------ */
  return (
    <div className="bg-body">
      {/* Sticky Topbar */}
      <Navbar expand="md" bg="light" className="border-bottom sticky-top">
        <Container>
          <Navbar.Brand href="#" className="fw-bold">
            Easy <span className="text-primary">DUI</span> Insurance
            {SHOW_VERSION && <Badge bg="secondary" className="ms-2">{VERSION}</Badge>}
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="topbar" />
          <Navbar.Collapse id="topbar" className="justify-content-end">
            <Nav className="align-items-center gap-2">
              <div className="d-none d-md-block text-body-secondary me-2">Speak with an agent:</div>
              <Button variant="outline-primary" href="tel:3033375434">{AGENT_PHONE_NUMBER}</Button>
              {/* <Button variant="primary" onClick={scrollToForm} className="ms-md-2">Get a Quote</Button> */}
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* HERO with background Carousel */}
      <section id="home" className="position-relative overflow-hidden" style={{ minHeight: "760px" }}>
        <div className="position-absolute top-0 start-0 end-0 bottom-0" style={{ zIndex: 0 }} aria-hidden>
          <Carousel
            activeIndex={activeSlide}
            onSelect={(i) => setActiveSlide(i)}
            controls
            indicators
            fade
            interval={3000}
            className="w-100 h-100"
          >
            {slides.map((s, idx) => (
              <Carousel.Item key={idx} className="w-100 h-100">
                <div
                  role="img"
                  aria-label={s.alt}
                  style={{
                    width: "100%",
                    height: "100%",
                    minHeight: 760,
                    backgroundImage: `linear-gradient(to right, rgba(0,0,0,0.55), rgba(0,0,0,0.25) 40%, rgba(0,0,0,0.10) 60%, rgba(0,0,0,0.00)), url('${s.src}')`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
                <Carousel.Caption className="text-start" style={{ bottom: "40%" }} />
              </Carousel.Item>
            ))}
          </Carousel>
        </div>

        {/* Foreground content */}
        <Container className="position-relative" style={{ zIndex: 1 }}>
          <Row className="align-items-start g-4 p-5">
            <Col lg={6} className="text-white">
              <div className="pe-lg-4">
                <h1 className="display-5 fw-bold" style={{ marginTop: "400px" }}>{active.title}</h1>
                <p className="lead mb-3">{active.subline}</p>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <Badge bg="light" text="dark">SR-22/FR-44 Available</Badge>
                  <Badge bg="light" text="dark">Multiple Carriers</Badge>
                  <Badge bg="light" text="dark">Licensed Advisors</Badge>
                </div>
                <div className="mt-4 d-flex gap-2">
                  <Button variant="outline-light" href="#faq">Common Questions</Button>
                </div>
              </div>
            </Col>

            <Col lg={6}>
              <Card className="shadow-lg" style={{ backdropFilter: "blur(6px)" }}>
                <Card.Header className="bg-white">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <div className="fw-semibold">Fast, No-Obligation Quote</div>
                      <small className="text-body-secondary"></small>
                    </div>
                    <Badge bg="success">Secure</Badge>
                  </div>
                </Card.Header>
                <Card.Body ref={formRef}>
                  {submitError && (
                    <Alert variant="danger" onClose={() => setSubmitError(null)} dismissible>
                      {submitError}
                    </Alert>
                  )}

                  {/* MODE TOGGLE + (Wizard) Stepper */}
                  <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                    <div className="d-flex align-items-center gap-2">
                      <Form.Check
                        type="switch"
                        id="modeSwitch"
                        label={isWizard ? "Wizard mode" : "Vertical mode"}
                        checked={isWizard}
                        onChange={(e) => {
                          setMode(e.currentTarget.checked ? "wizard" : "vertical");
                          setTouched({});
                          // if already submitted in vertical and switch to wizard, show step 4
                          if (submitOk && e.currentTarget.checked) setCurrentStep(3);
                          else setCurrentStep(0);
                        }}
                      />
                      <small className="text-muted">Switch to see all fields in one view.</small>
                    </div>

                    <div style={{ minWidth: 280 }}>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <Badge bg="secondary">Step {isWizard ? currentStep + 1 : (submitOk ? 2 : 1)} of {isWizard ? 4 : (submitOk ? 2 : 1)}</Badge>
                        <small className="text-muted">{isWizard ? steps[currentStep] : (submitOk ? "Confirmation" : "All fields")}</small>
                      </div>
                      <ProgressBar now={isWizard ? ((currentStep + 1) / 4) * 100 : (submitOk ? 100 : 50)} />
                    </div>
                  </div>

                  {/* DUI FORM START */}
                  <Form onSubmit={handleSubmit} noValidate>
                    {isWizard ? (
                      <>
                        <Row className="g-3">
                          {/* STEP 1: CONTACT INFO */}
                          {currentStep === 0 && (
                            <Col xs={12}>
                              <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
                                  <strong>Contact Info</strong>
                                </legend>

                                <Row className="g-3 mt-1">
                                  <Col md={6}>
                                    <Form.Label htmlFor="fullName">Full Name</Form.Label>
                                    <Form.Control
                                      id="fullName"
                                      value={fullName}
                                      onChange={(e) => setFullName(e.target.value)}
                                      placeholder="Jane Doe"
                                      required
                                      {...vp("fullName", V.fullName)}
                                    />
                                    <Form.Control.Feedback type="invalid">Please enter your full name.</Form.Control.Feedback>
                                  </Col>

                                  <Col md={6}>
                                    <Form.Label htmlFor="email">Email</Form.Label>
                                    <Form.Control
                                      id="email"
                                      type="email"
                                      value={email}
                                      onChange={(e) => setEmail(e.target.value)}
                                      placeholder="you@example.com"
                                      required
                                      {...vp("email", V.email)}
                                    />
                                    <Form.Control.Feedback type="invalid">Enter a valid email.</Form.Control.Feedback>
                                  </Col>

                                  <Col md={4}>
                                    <Form.Label htmlFor="phone">Phone</Form.Label>
                                    <Form.Control
                                      id="phone"
                                      inputMode="tel"
                                      value={phone}
                                      onChange={(e) => setPhone(e.target.value)}
                                      placeholder="(555) 123-4567"
                                      required
                                      {...vp("phone", V.phone)}
                                    />
                                    <Form.Control.Feedback type="invalid">Enter a 10-digit phone.</Form.Control.Feedback>
                                  </Col>

                                  <Col md={4}>
                                    <Form.Label htmlFor="state">Residence State</Form.Label>
                                    <Form.Select
                                      id="state"
                                      value={state}
                                      onChange={(e) => setState(e.target.value)}
                                      required
                                      {...vp("state", V.state)}
                                    >
                                      <option value="">Select state</option>
                                      {US_STATES.map((s) => (
                                        <option key={s.code} value={s.code}>{s.name}</option>
                                      ))}
                                    </Form.Select>
                                  </Col>

                                  <Col md={4}>
                                    <Form.Label htmlFor="zipCode">Zip Code</Form.Label>
                                    <Form.Control
                                      id="zipCode"
                                      inputMode="numeric"
                                      value={zipCode}
                                      onChange={(e) => setZipCode(e.target.value)}
                                      placeholder="#####"
                                      required
                                      {...vp("zip", V.zip)}
                                    />
                                    <Form.Control.Feedback type="invalid">Enter a 5-digit zip code.</Form.Control.Feedback>
                                  </Col>

                                  <Col md={4}>
                                    <Form.Label htmlFor="dlNumber">Driver’s License #</Form.Label>
                                    <Form.Control
                                      id="dlNumber"
                                      value={driversLicenseNumber}
                                      onChange={(e) => setDriversLicenseNumber(e.target.value)}
                                      placeholder="DL number"
                                      required
                                      {...vp("dlNum", V.dlNum)}
                                    />
                                    <Form.Control.Feedback type="invalid">Enter your license number.</Form.Control.Feedback>
                                  </Col>

                                  <Col md={4}>
                                    <Form.Label htmlFor="dlState">DL Issuing State</Form.Label>
                                    <Form.Select
                                      id="dlState"
                                      value={driversLicenseState}
                                      onChange={(e) => setDriversLicenseState(e.target.value)}
                                      required
                                      {...vp("dlState", V.dlState)}
                                    >
                                      <option value="">Select state</option>
                                      {US_STATES.map((s) => (
                                        <option key={s.code} value={s.code}>{s.name}</option>
                                      ))}
                                    </Form.Select>
                                  </Col>

                                  {/* DOB */}
                                  <Col md={4}>
                                    <Form.Label htmlFor="dob">Date of Birth</Form.Label>
                                    <Form.Control
                                      id="dob"
                                      type="date"
                                      value={dateOfBirth}
                                      onChange={(e) => setDateOfBirth(e.target.value)}
                                      required
                                      {...vp("dob", V.dob)}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                      Enter a valid DOB (16–100 years old, not in the future).
                                    </Form.Control.Feedback>
                                  </Col>
                                </Row>
                              </fieldset>
                            </Col>
                          )}

                          {/* STEP 2: DUI & FILING */}
                          {currentStep === 1 && (
                            <Col xs={12}>
                              <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
                                  <strong>DUI &amp; Filing</strong>
                                </legend>

                                <Row className="g-3 mt-1">
                                  <Col md={6}>
                                    <Form.Label htmlFor="duiCount"># of DUIs</Form.Label>
                                    <Form.Select
                                      id="duiCount"
                                      value={duiCount}
                                      onChange={(e) => setDuiCount(Number(e.target.value))}
                                      required
                                      {...vp("duiCount", V.duiCount)}
                                    >
                                      {Array.from({ length: 5 }).map((_, i) => (
                                        <option key={i} value={i}>{i}</option>
                                      ))}
                                    </Form.Select>
                                  </Col>

                                  <Col md={6}>
                                    <Form.Label htmlFor="duiDate">Most Recent DUI</Form.Label>
                                    <Form.Control
                                      id="duiDate"
                                      type="date"
                                      value={duiMostRecent}
                                      onChange={(e) => setDuiMostRecent(e.target.value)}
                                      required
                                      {...vp("duiDate", V.duiDate)}
                                    />
                                    <Form.Control.Feedback type="invalid">Enter a valid past date.</Form.Control.Feedback>
                                  </Col>

                                  <Col xs={12}>
                                    <Row className="g-3 align-items-end">
                                      <Col md={6}>
                                        <Form.Label htmlFor="filingType">Filing Type</Form.Label>
                                        <Form.Select
                                          id="filingType"
                                          value={filingType}
                                          onChange={(e) => setFilingType(e.target.value as FilingType)}
                                          {...vp("filingType", V.filingType)}
                                        >
                                          <option value="">Select filing type</option>
                                          <option value="NOT_SURE">Not sure</option>
                                          <option value="SR22">SR-22</option>
                                          <option value="FR44">FR-44</option>
                                        </Form.Select>
                                        <div className="form-text">We will confirm the right filing.<br />&nbsp;</div>
                                      </Col>
                                      <Col md={6}>
                                        <Form.Label htmlFor="filingState">Filing State</Form.Label>
                                        <Form.Select
                                          id="filingState"
                                          value={filingState}
                                          onChange={(e) => setFilingState(e.target.value)}
                                          {...vp("filingState", V.filingState)}
                                        >
                                          <option value="">Select state</option>
                                          {US_STATES.map((s) => (
                                            <option key={s.code} value={s.code}>{s.name}</option>
                                          ))}
                                        </Form.Select>
                                        <div className="form-text">FR-44 typically applies in Virginia or Florida.</div>
                                      </Col>
                                    </Row>
                                  </Col>
                                </Row>
                              </fieldset>
                            </Col>
                          )}

                          {/* STEP 3: VEHICLE & NOTES */}
                          {currentStep === 2 && (
                            <>
                              <Col xs={12} className="pt-2">
                                <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                  <legend className="float-none w-auto px-2 fs-6 text-muted mb-0"></legend>
                                  <div className="mt-2">
                                    <Form.Check
                                      type="switch"
                                      id="ownCarSwitch"
                                      label="I own a car ( optional vehicle details )"
                                      checked={ownCar}
                                      onChange={(e) => setOwnCar(e.currentTarget.checked)}
                                    />
                                  </div>

                                  {ownCar && (
                                    <Row className="g-3 mt-1">
                                      <Col md={3}>
                                        <Form.Label htmlFor="carYear">Year</Form.Label>
                                        <Form.Control
                                          id="carYear"
                                          inputMode="numeric"
                                          value={carYear}
                                          onChange={(e) => setCarYear(e.target.value)}
                                          placeholder="e.g., 2019"
                                          required
                                          {...vp("carYear", V.carYear)}
                                        />
                                        <Form.Control.Feedback type="invalid">Enter 4-digit year.</Form.Control.Feedback>
                                      </Col>
                                      <Col md={5}>
                                        <Form.Label htmlFor="carMake">Make</Form.Label>
                                        <Form.Control
                                          id="carMake"
                                          value={carMake}
                                          onChange={(e) => setCarMake(e.target.value)}
                                          placeholder="e.g., Toyota"
                                          required
                                          {...vp("carMake", V.carMake)}
                                        />
                                        <Form.Control.Feedback type="invalid">Enter vehicle make.</Form.Control.Feedback>
                                      </Col>
                                      <Col md={4}>
                                        <Form.Label htmlFor="carModel">Model</Form.Label>
                                        <Form.Control
                                          id="carModel"
                                          value={carModel}
                                          onChange={(e) => setCarModel(e.target.value)}
                                          placeholder="e.g., Camry"
                                          required
                                          {...vp("carModel", V.carModel)}
                                        />
                                        <Form.Control.Feedback type="invalid">Enter vehicle model.</Form.Control.Feedback>
                                      </Col>

                                      <Col md={5}>
                                        <Form.Label htmlFor="carVIN">VIN (optional)</Form.Label>
                                        <InputGroup hasValidation>
                                          <Form.Control
                                            id="carVIN"
                                            value={carVIN}
                                            onChange={(e) => setCarVIN(e.target.value.toUpperCase())}
                                            onBlur={() => markTouched("carVIN")}
                                            placeholder="17 characters"
                                            maxLength={17}
                                            isValid={isTouched("carVIN") && carVIN.trim() !== "" && vinLen === 17}
                                            isInvalid={isTouched("carVIN") && carVIN.trim() !== "" && vinLen !== 17}
                                            className={isTouched("carVIN") && carVIN.trim() !== "" && vinLen === 17 ? "bg-success-subtle" : undefined}
                                            aria-describedby="vinHelp"
                                          />
                                          <Form.Control.Feedback type="invalid">
                                            VIN must be 17 characters (you entered {vinLen}/17) <br></br>Leave blank if unsure.
                                          </Form.Control.Feedback>
                                        </InputGroup>
                                        <div
                                          id="vinHelp"
                                          className={
                                            "form-text" +
                                            (carVIN.trim() !== "" && vinLen !== 17
                                              ? " text-danger"
                                              : carVIN.trim() !== "" && vinLen === 17
                                              ? " text-success"
                                              : "")
                                          }
                                        >
                                          VIN length: <strong>{vinLen}/17</strong>
                                          {carVIN.trim() !== "" && vinLen === 17 ? " • looks good ✓" : ""}
                                        </div>
                                      </Col>

                                      <Col md={3}>
                                        <Form.Label htmlFor="carMiles">Annual Miles</Form.Label>
                                        <Form.Control
                                          id="carMiles"
                                          inputMode="text"
                                          type="text"
                                          value={carApproximateAnnualMileage}
                                          onChange={(e) => setCarApproximateAnnualMileage(e.target.value)}
                                          placeholder="e.g., 12,000"
                                          {...vp("carMiles", V.carMiles || carApproximateAnnualMileage.trim() === "")}
                                        />
                                        <div className="form-text">Approximate Miles. Commas are ok. Leave blank if unsure.</div>
                                      </Col>

                                      <Col md={4}>
                                        <Form.Label htmlFor="carUse">Primary Use</Form.Label>
                                        <Form.Select
                                          id="carUse"
                                          value={carPrimaryUse}
                                          onChange={(e) => setCarPrimaryUse(e.target.value as any)}
                                          required
                                          {...vp("carUse", V.carUse)}
                                        >
                                          <option value="">Select use</option>
                                          <option value="commute">Commuting</option>
                                          <option value="business">Business</option>
                                          <option value="pleasure">Pleasure</option>
                                          <option value="rideshare">Rideshare</option>
                                        </Form.Select>
                                      </Col>
                                    </Row>
                                  )}
                                </fieldset>
                              </Col>

                              {/* Notes */}
                              <Col md={12}>
                                <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                  <Form.Label htmlFor="notes">Notes (optional - additional helpful / important info)</Form.Label>
                                  <Form.Control
                                    id="notes"
                                    as="textarea"
                                    rows={3}
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder="Other helpful / important information?"
                                    {...vp("notes", notes.trim().length >= 2 || notes.trim().length === 0)}
                                  />
                                </fieldset>
                              </Col>
                            </>
                          )}

                          {/* STEP 4: CONFIRMATION */}
                          {currentStep === 3 && (
                            <Col xs={12}>
                              <ConfirmationBlock />
                            </Col>
                          )}
                        </Row>

                        {/* Wizard controls */}

                        {/* Wizard controls */}
                            {currentStep < 3 && (
                            <div className="d-flex justify-content-between mt-3">
                                <Button
                                variant="outline-secondary"
                                onClick={goBack}
                                disabled={currentStep === 0}
                                >
                                Previous
                                </Button>

                                {currentStep < 2 ? (
                                <Button onClick={goNext} disabled={!isStepValid(currentStep)}>
                                    Next
                                </Button>
                                ) : (
                                <Button
                                    type="submit"
                                    variant="primary"
                                    size="lg"
                                    disabled={submitting || !isFormValid()}
                                >
                                    {submitting ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" /> Submitting...
                                    </>
                                    ) : (
                                    <>Get My Quote</>
                                    )}
                                </Button>
                                )}
                            </div>
                            )}


                        {/* <div className="d-flex justify-content-between mt-3">
                          <Button variant="outline-secondary" onClick={goBack} disabled={currentStep === 0 || currentStep === 3}>
                            Previous
                          </Button>

                          {currentStep < 2 && (
                            <Button onClick={goNext} disabled={!isStepValid(currentStep)}>
                              Next
                            </Button>
                          )}

                          {currentStep === 2 && (
                            <Button type="submit" variant="primary" size="lg" disabled={submitting || !isFormValid()}>
                              {submitting ? (<><Spinner animation="border" size="sm" className="me-2" /> Submitting...</>) : (<>Get My Quote</>)}
                            </Button>
                          )}

                          {currentStep === 3 && (
                            <Button variant="primary" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
                              Back to Top
                            </Button>
                            // <span></span>
                          )}
                        </div> */}
                      </>
                    ) : (
                      /* ---------- VERTICAL (all-in-one) ---------- */
                      <>
                        {!submitOk ? (
                          <>
                            <Row className="g-3">
                              {/* Contact & License */}
                              <Col xs={12}>
                                <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                  <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
                                    <strong>Contact Info</strong>
                                  </legend>

                                  <Row className="g-3 mt-1">
                                    <Col md={6}>
                                      <Form.Label htmlFor="fullNameV">Full Name</Form.Label>
                                      <Form.Control
                                        id="fullNameV"
                                        value={fullName}
                                        onChange={(e) => setFullName(e.target.value)}
                                        placeholder="Jane Doe"
                                        required
                                        {...vp("fullName", V.fullName)}
                                      />
                                      <Form.Control.Feedback type="invalid">Please enter your full name.</Form.Control.Feedback>
                                    </Col>

                                    <Col md={6}>
                                      <Form.Label htmlFor="emailV">Email</Form.Label>
                                      <Form.Control
                                        id="emailV"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="you@example.com"
                                        required
                                        {...vp("email", V.email)}
                                      />
                                      <Form.Control.Feedback type="invalid">Enter a valid email.</Form.Control.Feedback>
                                    </Col>

                                    <Col md={4}>
                                      <Form.Label htmlFor="phoneV">Phone</Form.Label>
                                      <Form.Control
                                        id="phoneV"
                                        inputMode="tel"
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        placeholder="(555) 123-4567"
                                        required
                                        {...vp("phone", V.phone)}
                                      />
                                      <Form.Control.Feedback type="invalid">Enter a 10-digit phone.</Form.Control.Feedback>
                                    </Col>

                                    <Col md={4}>
                                      <Form.Label htmlFor="stateV">Residence State</Form.Label>
                                      <Form.Select
                                        id="stateV"
                                        value={state}
                                        onChange={(e) => setState(e.target.value)}
                                        required
                                        {...vp("state", V.state)}
                                      >
                                        <option value="">Select state</option>
                                        {US_STATES.map((s) => (
                                          <option key={s.code} value={s.code}>{s.name}</option>
                                        ))}
                                      </Form.Select>
                                    </Col>

                                    <Col md={4}>
                                      <Form.Label htmlFor="zipCodeV">Zip Code</Form.Label>
                                      <Form.Control
                                        id="zipCodeV"
                                        inputMode="numeric"
                                        value={zipCode}
                                        onChange={(e) => setZipCode(e.target.value)}
                                        placeholder="#####"
                                        required
                                        {...vp("zip", V.zip)}
                                      />
                                      <Form.Control.Feedback type="invalid">Enter a 5-digit zip code.</Form.Control.Feedback>
                                    </Col>

                                    <Col md={4}>
                                      <Form.Label htmlFor="dlNumberV">Driver’s License #</Form.Label>
                                      <Form.Control
                                        id="dlNumberV"
                                        value={driversLicenseNumber}
                                        onChange={(e) => setDriversLicenseNumber(e.target.value)}
                                        placeholder="DL number"
                                        required
                                        {...vp("dlNum", V.dlNum)}
                                      />
                                      <Form.Control.Feedback type="invalid">Enter your license number.</Form.Control.Feedback>
                                    </Col>

                                    <Col md={4}>
                                      <Form.Label htmlFor="dlStateV">DL Issuing State</Form.Label>
                                      <Form.Select
                                        id="dlStateV"
                                        value={driversLicenseState}
                                        onChange={(e) => setDriversLicenseState(e.target.value)}
                                        required
                                        {...vp("dlState", V.dlState)}
                                      >
                                        <option value="">Select state</option>
                                        {US_STATES.map((s) => (
                                          <option key={s.code} value={s.code}>{s.name}</option>
                                        ))}
                                      </Form.Select>
                                    </Col>

                                    <Col md={4}>
                                      <Form.Label htmlFor="dobV">Date of Birth</Form.Label>
                                      <Form.Control
                                        id="dobV"
                                        type="date"
                                        value={dateOfBirth}
                                        onChange={(e) => setDateOfBirth(e.target.value)}
                                        required
                                        {...vp("dob", V.dob)}
                                      />
                                      <Form.Control.Feedback type="invalid">
                                        Enter a valid DOB (16–100 years old, not in the future).
                                      </Form.Control.Feedback>
                                    </Col>
                                  </Row>
                                </fieldset>
                              </Col>

                              {/* DUI & Filing */}
                              <Col xs={12}>
                                <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                  <legend className="float-none w-auto px-2 fs-6 text-muted mb-0">DUI &amp; Filing</legend>
                                  <Row className="g-3 mt-1">
                                    <Col md={6}>
                                      <Form.Label htmlFor="duiCountV"># of DUIs</Form.Label>
                                      <Form.Select
                                        id="duiCountV"
                                        value={duiCount}
                                        onChange={(e) => setDuiCount(Number(e.target.value))}
                                        required
                                        {...vp("duiCount", V.duiCount)}
                                      >
                                        {Array.from({ length: 5 }).map((_, i) => (
                                          <option key={i} value={i}>{i}</option>
                                        ))}
                                      </Form.Select>
                                    </Col>

                                    <Col md={6}>
                                      <Form.Label htmlFor="duiDateV">Most Recent DUI</Form.Label>
                                      <Form.Control
                                        id="duiDateV"
                                        type="date"
                                        value={duiMostRecent}
                                        onChange={(e) => setDuiMostRecent(e.target.value)}
                                        required
                                        {...vp("duiDate", V.duiDate)}
                                      />
                                      <Form.Control.Feedback type="invalid">Enter a valid past date.</Form.Control.Feedback>
                                    </Col>

                                    <Col xs={12}>
                                      <Row className="g-3 align-items-end">
                                        <Col md={6}>
                                          <Form.Label htmlFor="filingTypeV">Filing Type</Form.Label>
                                          <Form.Select
                                            id="filingTypeV"
                                            value={filingType}
                                            onChange={(e) => setFilingType(e.target.value as FilingType)}
                                            {...vp("filingType", V.filingType)}
                                          >
                                            <option value="">Select filing type</option>
                                            <option value="NOT_SURE">Not sure</option>
                                            <option value="SR22">SR-22</option>
                                            <option value="FR44">FR-44</option>
                                          </Form.Select>
                                          <div className="form-text">We will confirm the right filing.<br></br>&nbsp;</div>
                                        </Col>
                                        <Col md={6}>
                                          <Form.Label htmlFor="filingStateV">Filing State</Form.Label>
                                          <Form.Select
                                            id="filingStateV"
                                            value={filingState}
                                            onChange={(e) => setFilingState(e.target.value)}
                                            {...vp("filingState", V.filingState)}
                                          >
                                            <option value="">Select state</option>
                                            {US_STATES.map((s) => (
                                              <option key={s.code} value={s.code}>{s.name}</option>
                                            ))}
                                          </Form.Select>
                                          <div className="form-text">FR-44 typically applies in Virginia or Florida.</div>
                                        </Col>
                                      </Row>
                                    </Col>
                                  </Row>
                                </fieldset>
                              </Col>

                              {/* Vehicle */}
                              <Col xs={12} className="pt-2">
                                <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                  <legend className="float-none w-auto px-2 fs-6 text-muted mb-0"></legend>
                                  <div className="mt-2">
                                    <Form.Check
                                      type="switch"
                                      id="ownCarSwitchV"
                                      label="I own a car - optional (vehicle details)"
                                      checked={ownCar}
                                      onChange={(e) => setOwnCar(e.currentTarget.checked)}
                                    />
                                  </div>

                                  {ownCar && (
                                    <Row className="g-3 mt-1">
                                      <Col md={3}>
                                        <Form.Label htmlFor="carYearV">Year</Form.Label>
                                        <Form.Control
                                          id="carYearV"
                                          inputMode="numeric"
                                          value={carYear}
                                          onChange={(e) => setCarYear(e.target.value)}
                                          placeholder="e.g., 2019"
                                          required
                                          {...vp("carYear", V.carYear)}
                                        />
                                        <Form.Control.Feedback type="invalid">Enter 4-digit year.</Form.Control.Feedback>
                                      </Col>
                                      <Col md={5}>
                                        <Form.Label htmlFor="carMakeV">Make</Form.Label>
                                        <Form.Control
                                          id="carMakeV"
                                          value={carMake}
                                          onChange={(e) => setCarMake(e.target.value)}
                                          placeholder="e.g., Toyota"
                                          required
                                          {...vp("carMake", V.carMake)}
                                        />
                                        <Form.Control.Feedback type="invalid">Enter vehicle make.</Form.Control.Feedback>
                                      </Col>
                                      <Col md={4}>
                                        <Form.Label htmlFor="carModelV">Model</Form.Label>
                                        <Form.Control
                                          id="carModelV"
                                          value={carModel}
                                          onChange={(e) => setCarModel(e.target.value)}
                                          placeholder="e.g., Camry"
                                          required
                                          {...vp("carModel", V.carModel)}
                                        />
                                        <Form.Control.Feedback type="invalid">Enter vehicle model.</Form.Control.Feedback>
                                      </Col>

                                      <Col md={5}>
                                        <Form.Label htmlFor="carVINV">VIN (optional)</Form.Label>
                                        <InputGroup hasValidation>
                                          <Form.Control
                                            id="carVINV"
                                            value={carVIN}
                                            onChange={(e) => setCarVIN(e.target.value.toUpperCase())}
                                            onBlur={() => markTouched("carVIN")}
                                            placeholder="17 characters"
                                            maxLength={17}
                                            isValid={isTouched("carVIN") && carVIN.trim() !== "" && vinLen === 17}
                                            isInvalid={isTouched("carVIN") && carVIN.trim() !== "" && vinLen !== 17}
                                            className={isTouched("carVIN") && carVIN.trim() !== "" && vinLen === 17 ? "bg-success-subtle" : undefined}
                                            aria-describedby="vinHelpV"
                                          />
                                          <Form.Control.Feedback type="invalid">
                                            VIN must be 17 characters (you entered {vinLen}/17).  <br></br>Leave blank if unsure.
                                          </Form.Control.Feedback>
                                        </InputGroup>
                                        <div
                                          id="vinHelpV"
                                          className={
                                            "form-text" +
                                            (carVIN.trim() !== "" && vinLen !== 17
                                              ? " text-danger"
                                              : carVIN.trim() !== "" && vinLen === 17
                                              ? " text-success"
                                              : "")
                                          }
                                        >
                                          VIN length: <strong>{vinLen}/17</strong>
                                          {carVIN.trim() !== "" && vinLen === 17 ? " • looks good ✓" : ""}
                                        </div>
                                      </Col>

                                      <Col md={4}>
                                        <Form.Label htmlFor="carMilesV">Annual Miles</Form.Label>
                                        <Form.Control
                                          id="carMilesV"
                                          inputMode="text"
                                          type="text"
                                          value={carApproximateAnnualMileage}
                                          onChange={(e) => setCarApproximateAnnualMileage(e.target.value)}
                                          placeholder="e.g., 12,000"
                                          {...vp("carMiles", V.carMiles || carApproximateAnnualMileage.trim() === "")}
                                        />
                                        <div className="form-text">Approximate Miles. You can include commas. Leave blank if unsure.</div>
                                      </Col>

                                      <Col md={3}>
                                        <Form.Label htmlFor="carUseV">Primary Use</Form.Label>
                                        <Form.Select
                                          id="carUseV"
                                          value={carPrimaryUse}
                                          onChange={(e) => setCarPrimaryUse(e.target.value as any)}
                                          required
                                          {...vp("carUse", V.carUse)}
                                        >
                                          <option value="">Select use</option>
                                          <option value="commute">Commuting</option>
                                          <option value="business">Business</option>
                                          <option value="pleasure">Pleasure</option>
                                          <option value="rideshare">Rideshare</option>
                                        </Form.Select>
                                      </Col>
                                    </Row>
                                  )}
                                </fieldset>
                              </Col>

                              {/* Notes */}
                              <Col md={12}>
                                <Form.Label htmlFor="notesV">Notes (optional - additional helpful / important info)</Form.Label>
                                <Form.Control
                                  id="notesV"
                                  as="textarea"
                                  rows={3}
                                  value={notes}
                                  onChange={(e) => setNotes(e.target.value)}
                                  placeholder="Other helpful / important information?"
                                  {...vp("notes", notes.trim().length >= 2 || notes.trim().length === 0)}
                                />
                              </Col>
                            </Row>

                            {/* Submit */}
                            <div className="d-grid mt-3">
                              <Button type="submit" variant="primary" size="lg" disabled={!valid || submitting}>
                                {submitting ? (<><Spinner animation="border" size="sm" className="me-2" /> Submitting...</>) : (<>Get My Quote</>)}
                              </Button>
                              <div className="text-center mt-2 text-body-secondary" style={{ fontSize: "0.9rem" }}>
                                By clicking, you agree we may contact you about insurance options. No spam. You can opt out anytime.
                              </div>
                            </div>
                          </>
                        ) : (
                          // Inline confirmation in vertical mode
                          <ConfirmationBlock />
                        )}
                      </>
                    )}
                  </Form>
                  {/* DUI FORM END */}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Why Choose Us - SAVE FOR LATER */}
      {/* 
      <section className="py-5 bg-body">
        <Container>
          <Row className="mb-4">
            <Col lg={8}>
              <h2 className="fw-bold mb-1">Why Choose Easy DUI Insurance</h2>
              <p className="text-body-secondary mb-0">We compare carriers and guide you to a compliant, affordable policy.</p>
            </Col>
          </Row>

          <Row xs={1} md={3} className="g-3">
            <Col><Feature emoji="⚡" title="Fast Quotes" text="Most drivers get options the same day, often within minutes." /></Col>
            <Col><Feature emoji="🛡️" title="SR-22/FR-44" text="State filings available where required to reinstate or maintain your license." /></Col>
            <Col><Feature emoji="💬" title="Human Help" text="Licensed advisors answer questions and explain coverage plainly." /></Col>
          </Row>
        </Container>
      </section> 
      
      */}

      {/* How It Works */}
      <section className="py-5 bg-light border-top border-bottom">
        <Container>
          <Row className="mb-4">
            <Col lg={8}>
              <h2 className="fw-bold mb-1">How It Works</h2>
              <p className="text-body-secondary mb-0">Three simple steps to get covered.</p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col md={4}><StepCard n={1} title="Tell us about you" text="Share basic info about your situation and state requirements." /></Col>
            <Col md={4}><StepCard n={2} title="We compare options" text="We shop multiple carriers for compliant, affordable coverage." /></Col>
            <Col md={4}><StepCard n={3} title="You choose & bind" text="Pick your plan and start coverage.  We will send filings ( SR-22, FR-44 ) as required." /></Col>
          </Row>
        </Container>
      </section>

      {/* Social Proof - SAVE FOR LATER */}
      {/* 
      <section className="py-5">
        <Container>
          <Row className="mb-4">
            <Col lg={8}>
              <h2 className="fw-bold mb-1">Customers Say</h2>
              <p className="text-body-secondary mb-0">Real experiences from drivers who secured coverage after a DUI.</p>
            </Col>
          </Row>
          <Row xs={1} md={3} className="g-3">
            <Col><Testimonial quote="They explained SR-22 better than anyone and got me a fair price." name="Alex R." detail="Colorado • SR-22" /></Col>
            <Col><Testimonial quote="Quick, clear, and no pressure. I had options the same morning." name="María L." detail="Texas • 1 DUI" /></Col>
            <Col><Testimonial quote="Handled my FR-44 filing and I was back on the road." name="Derrick K." detail="Florida • FR-44" /></Col>
          </Row>
        </Container>
      </section> 
      */}

      {/* FAQ */}
      <section id="faq" className="py-5 bg-light border-top">
        <Container>
          <Row className="mb-4">
            <Col lg={8}>
              <h2 className="fw-bold mb-1">Frequently Asked Questions</h2>
              <p className="text-body-secondary mb-0">Short answers to common concerns.</p>
            </Col>
          </Row>


          <FAQ_Component_v02b_Simple 
          faqs={duiInsuranceFAQ_CONTENT_v01}  
          answerClassName="simpleAccordion-Answer" 
          questionHeaderClassName="simpleAaccordion-header-question" 
          openFirst />


          {/* <FAQ_Component_MASTER_v02_WithSUB faqDataDocument={faqDataDocument}></FAQ_Component_MASTER_v02_WithSUB> */}

          {/* <FAQ_Component_MASTER_v02_WithSUB faqDataDocument={duiInsuranceFAQ_CONTENT_v01}></FAQ_Component_MASTER_v02_WithSUB> */}
      
        </Container>
      </section>

      {/* Footer */}
      <footer className="py-4">
        <Container>
          <Row className="align-items-center">
            <Col md={6} className="small text-body-secondary">
              © {new Date().getFullYear()} Easy DUI Insurance. All rights reserved.
              <div className="mt-2">
                Insurance services subject to licensing and state availability. This page is for general information and not legal advice.
              </div>
            </Col>
            <Col md={6} className="d-flex justify-content-md-end mt-3 mt-md-0 gap-2">
              <Button variant="secondary" size="sm" href="tel:3033375434">Call {AGENT_PHONE_NUMBER}</Button>
            </Col>
          </Row>
        </Container>
      </footer>
    </div>
  );
};

export default EASY_DUI_LandingPage_v02h_WIZARD;
